/**
 * /checkin/conversation — mobile-first Dr. C conversation view.
 *
 * Design brief (per master spec §22-G):
 *   Rounded conversation bubbles. Message list with Dr. C on the left,
 *   member on the right. Persistent bottom composer with mic + send.
 *   Per-assistant-message audio play (fetched blob URL, no key leak).
 *   Talk Credit indicator (Phase 3: shows Complimentary Check-In status).
 *   NO robot avatar (spec forbids it) — Dr. C's initial is used.
 *   The mic uses browser Web Speech API; failure gracefully degrades to
 *   text-only. Audio failure never removes the written response.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, Send, Mic, Play, Pause, StopCircle, Sparkles, ChevronLeft } from "lucide-react";
import { CheckInShell, CHECKIN_PALETTE } from "./_shell";
import { checkinApi, getToken, clearAuth } from "@/lib/checkinApi";

function DrCAvatar({ small = false }) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-serif ${
        small ? "w-7 h-7 text-[13px]" : "w-9 h-9 text-[16px]"
      }`}
      style={{
        background: `linear-gradient(135deg, ${CHECKIN_PALETTE.purple}, #3A1362)`,
        color: "white",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
      }}
      aria-hidden="true"
    >
      C
    </span>
  );
}

function Bubble({ msg, onPlayAudio, playing, loadingAudio }) {
  const isUser = msg.role === "user";
  return (
    <div className={`w-full flex ${isUser ? "justify-end" : "justify-start"} mt-3`}
         data-testid={`bubble-${msg.role}`}>
      {!isUser && (
        <div className="mr-2 flex-shrink-0 mt-1">
          <DrCAvatar small />
        </div>
      )}
      <div className="max-w-[85%]">
        <div
          className="rounded-2xl px-4 py-3 text-[14.5px] leading-relaxed whitespace-pre-wrap"
          style={
            isUser
              ? { background: CHECKIN_PALETTE.purple, color: "white",
                  borderBottomRightRadius: 6 }
              : { background: "white", color: CHECKIN_PALETTE.ink,
                  border: `1px solid ${CHECKIN_PALETTE.silverSoft}`,
                  borderBottomLeftRadius: 6 }
          }
        >
          {msg.content}
        </div>
        {!isUser && (
          <button
            type="button"
            onClick={() => onPlayAudio(msg.id)}
            data-testid={`audio-play-${msg.id}`}
            className="mt-1 inline-flex items-center gap-1 text-[11px] hover:underline"
            style={{ color: CHECKIN_PALETTE.purple }}
            disabled={loadingAudio}
          >
            {loadingAudio ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> Preparing audio…
              </>
            ) : playing ? (
              <>
                <Pause className="w-3 h-3" /> Pause
              </>
            ) : (
              <>
                <Play className="w-3 h-3" /> Play in Dr. C&rsquo;s voice
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function ComplimentaryBanner({ state, accessType, invitation, balance, hasMembership,
                                 onResumePaid, resuming }) {
  if (state === "paused_awaiting_paid") {
    const canResume = hasMembership || (Number(balance) || 0) >= 1;
    return (
      <div
        className="rounded-2xl p-4"
        style={{
          background: "linear-gradient(135deg, rgba(92,30,138,0.06), rgba(139,92,246,0.05))",
          border: `1px solid ${CHECKIN_PALETTE.purpleSoft}`,
        }}
        data-testid="complimentary-consumed-banner"
      >
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold"
             style={{ color: CHECKIN_PALETTE.purple }}>
          Your complimentary check-in is complete
        </div>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "#3A3348" }}>
          {invitation || "Whenever you're ready, add Talk Credits or a membership and we'll pick up right where we left off."}
        </p>
        <div className="mt-1 text-[11px]" style={{ color: "#6C6478" }} data-testid="consumed-balance-line">
          {hasMembership
            ? "Your membership covers this — tap to resume."
            : `Talk Credits available: ${Number(balance) || 0}. Continuing uses 1 credit.`}
        </div>
        <button
          type="button"
          onClick={() => {
            if (canResume) onResumePaid();
            else window.location.assign("/checkin/plans");
          }}
          disabled={resuming}
          data-testid="consumed-cta-add-credits"
          className="mt-3 rounded-full px-5 py-2 text-[13px] font-medium text-white disabled:opacity-60"
          style={{ background: CHECKIN_PALETTE.purple }}
        >
          {resuming
            ? "Resuming…"
            : canResume
              ? "Continue with Dr. C"
              : "Add Talk Credits"}
        </button>
      </div>
    );
  }
  if (accessType && accessType !== "complimentary") {
    // On a paid (talk_credit or membership) conversation, show a subtle
    // access pill instead of the complimentary banner.
    return (
      <div className="rounded-2xl px-3 py-2 text-[11px] text-center"
           style={{ background: "rgba(63,143,106,0.08)", color: "#3F8F6A" }}
           data-testid="paid-access-banner">
        <Sparkles className="inline w-3 h-3 mr-1 align-[-1px]" />
        {accessType === "membership" ? "Member access" : "Talk Credit session"}
      </div>
    );
  }
  return (
    <div className="rounded-2xl px-3 py-2 text-[11px] text-center"
         style={{ background: "rgba(92,30,138,0.05)", color: CHECKIN_PALETTE.purple }}
         data-testid="complimentary-active-banner">
      <Sparkles className="inline w-3 h-3 mr-1 align-[-1px]" />
      Your Complimentary Check-In
    </div>
  );
}

export default function CheckInConversation() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [convState, setConvState] = useState("active");
  const [accessType, setAccessType] = useState("complimentary");
  const [invitation, setInvitation] = useState("");
  const [balance, setBalance] = useState(0);
  const [hasMembership, setHasMembership] = useState(false);
  const [sendEnabled, setSendEnabled] = useState(true);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [audioMsgId, setAudioMsgId] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudioFor, setLoadingAudioFor] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const audioRef = useRef(null);
  const listRef = useRef(null);

  // Mic (Web Speech API) — graceful fallback if unavailable.
  const [micActive, setMicActive] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/checkin", { replace: true });
      return;
    }
    // Support opening a SPECIFIC historic conversation via ?id=xxx from
    // the "My Check-Ins" list. Reading is always allowed, regardless of
    // balance / membership state.
    const params = new URLSearchParams(window.location.search);
    const requestedId = params.get("id");
    (async () => {
      try {
        let out;
        if (requestedId) {
          const detail = await checkinApi.readConversation(requestedId);
          const conv = detail.conversation || {};
          out = {
            conversation_id: conv.id,
            state: conv.state, access_type: conv.access_type,
            messages: detail.messages || [],
            paused_invitation: conv.paused_invitation || "",
            balance: detail.balance, has_membership: detail.has_membership,
          };
          setReadOnlyMode(!detail.send_enabled);
          setSendEnabled(!!detail.send_enabled);
        } else {
          out = await checkinApi.startConversation();
          setSendEnabled(true);
        }
        setConversationId(out.conversation_id);
        const list = Array.isArray(out.messages) && out.messages.length
          ? out.messages.map(m => ({ id: m.id, role: m.role, content: m.content }))
          : (out.assistant_message
              ? [{ id: out.assistant_message.id, role: "assistant",
                     content: out.assistant_message.content }]
              : []);
        setMessages(list);
        setConvState(out.state || "active");
        setAccessType(out.access_type || "complimentary");
        setInvitation(out.paused_invitation || "");
        setBalance(Number(out.balance) || 0);
        setHasMembership(Boolean(out.has_membership));
        setLoading(false);
      } catch (e) {
        if (e.status === 401) {
          clearAuth();
          navigate("/checkin/signin", { replace: true });
        } else if (e.status === 403 && (
          e.code === "phone_verification_required" || e.code === "grace_exhausted"
        )) {
          navigate(`/checkin/verify?from=${encodeURIComponent(location.pathname + location.search)}`,
                    { replace: true });
        } else {
          setErr(e.message || "Couldn't open the conversation.");
          setLoading(false);
        }
      }
    })();
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch { /* ignore */ }
      }
    };
  }, [navigate]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages.length]);

  async function onSend() {
    const t = text.trim();
    if (!t || sending || convState === "paused_awaiting_paid") return;
    setText("");
    setSending(true);
    setErr("");
    const tempId = `local-${Date.now()}`;
    setMessages((m) => [...m, { id: tempId, role: "user", content: t }]);
    try {
      const out = await checkinApi.sendTurn(conversationId, t);
      setMessages((m) => {
        const replaced = m.map((x) =>
          x.id === tempId ? { ...x, id: out.user_message_id } : x
        );
        return [...replaced, {
          id: out.assistant_message_id,
          role: "assistant",
          content: out.assistant_text,
        }];
      });
      if (out.conversation_state) setConvState(out.conversation_state);
      if (out.complimentary_invitation) setInvitation(out.complimentary_invitation);
    } catch (e) {
      if (e.status === 402) {
        // Turn was rejected because the conversation is paused. Freeze
        // the thread and surface the banner — do NOT wipe the messages.
        setConvState("paused_awaiting_paid");
        setInvitation(e.raw?.detail?.message || e.message || "");
        if (typeof e.raw?.detail?.balance === "number") setBalance(e.raw.detail.balance);
        if (typeof e.raw?.detail?.has_membership === "boolean") setHasMembership(e.raw.detail.has_membership);
      } else {
        setErr(e.message || "Something went wrong.");
      }
    } finally {
      setSending(false);
    }
  }

  /** Unlock the paused conversation using membership access or a Talk
   * Credit debit. Idempotent server-side, and re-entrancy-guarded here
   * so double-clicks never dispatch twice. */
  async function onResumePaid() {
    if (resuming) return;
    setResuming(true);
    setErr("");
    try {
      const out = await checkinApi.resumePaidConversation();
      setConvState("active");
      setAccessType(out.access_type || "talk_credit");
      setBalance(Number(out.balance) || 0);
      setHasMembership(Boolean(out.has_membership));
      setInvitation("");
    } catch (e) {
      if (e.status === 402 && e.code === "insufficient_credits") {
        // No credits AND no membership → send them to Plans.
        window.location.assign("/checkin/plans");
        return;
      }
      setErr(e.message || "Couldn't resume the conversation.");
    } finally {
      setResuming(false);
    }
  }

  async function onPlayAudio(msgId) {
    if (loadingAudioFor === msgId) return;
    // Toggle pause if same message currently playing.
    if (audioMsgId === msgId && audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
        setAudioPlaying(false);
      } else {
        try { await audioRef.current.play(); setAudioPlaying(true); }
        catch { /* autoplay blocked */ }
      }
      return;
    }
    // Different message — revoke old blob, fetch new.
    if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); }
    setLoadingAudioFor(msgId);
    try {
      const url = await checkinApi.fetchAudioBlobUrl(msgId);
      setAudioUrl(url);
      setAudioMsgId(msgId);
      // Play on the next tick after src updates.
      setTimeout(async () => {
        if (audioRef.current) {
          try { await audioRef.current.play(); setAudioPlaying(true); }
          catch { /* autoplay blocked */ }
        }
      }, 30);
    } catch (e) {
      setErr(e.message || "Audio unavailable.");
    } finally {
      setLoadingAudioFor(null);
    }
  }

  function toggleMic() {
    if (micActive) {
      try { recognitionRef.current?.stop(); } catch { /* ignore */ }
      setMicActive(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setErr("Voice input isn't supported in this browser.");
      return;
    }
    const rec = new SR();
    rec.lang = navigator.language || "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    let base = text;
    rec.onresult = (ev) => {
      let interim = "";
      let finalText = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const t = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += t;
        else interim += t;
      }
      setText((base + " " + finalText + " " + interim).trim());
      if (finalText) base = (base + " " + finalText).trim();
    };
    rec.onend = () => setMicActive(false);
    rec.onerror = () => setMicActive(false);
    recognitionRef.current = rec;
    try { rec.start(); setMicActive(true); }
    catch { setMicActive(false); }
  }

  if (loading) {
    return (
      <CheckInShell testid="checkin-conversation-loading">
        <div className="mt-24 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: CHECKIN_PALETTE.purple }} />
        </div>
      </CheckInShell>
    );
  }

  return (
    <CheckInShell testid="checkin-conversation">
      <div className="pt-2 flex items-center justify-between">
        <button onClick={() => navigate("/checkin/home")}
                className="flex items-center gap-1 text-xs"
                style={{ color: CHECKIN_PALETTE.purple }}
                data-testid="conversation-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Home
        </button>
        <div className="flex items-center gap-2">
          <DrCAvatar small />
          <div className="text-[13px] font-medium" style={{ color: CHECKIN_PALETTE.ink }}>
            Dr. C
          </div>
        </div>
        <div className="w-8" />
      </div>

      <div className="mt-2 flex items-center justify-end">
        <button
          type="button"
          onClick={() => navigate("/checkin/history")}
          className="text-[11px] underline"
          style={{ color: CHECKIN_PALETTE.purple }}
          data-testid="convo-history-link"
        >
          My Check-Ins
        </button>
      </div>

      {readOnlyMode && (
        <div
          className="mt-3 rounded-2xl border p-4"
          style={{ borderColor: CHECKIN_PALETTE.silverSoft, background: "white" }}
          data-testid="readonly-notice"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold"
               style={{ color: CHECKIN_PALETTE.purple }}>
            Read-only
          </div>
          <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "#2E2439" }}>
            Your check-in is saved. You can return anytime to reread this
            conversation. Add Talk Credits or choose a membership when
            you&rsquo;re ready to continue with Dr.&nbsp;C.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => navigate("/checkin/plans")}
                    className="rounded-full px-4 py-1.5 text-[12px] font-medium text-white"
                    style={{ background: CHECKIN_PALETTE.purple }}
                    data-testid="readonly-add-credits">
              Add Talk Credits
            </button>
            <button onClick={() => navigate("/checkin/plans?scroll=memberships")}
                    className="rounded-full px-4 py-1.5 text-[12px] font-medium"
                    style={{
                      background: "white", color: CHECKIN_PALETTE.purple,
                      border: `1px solid ${CHECKIN_PALETTE.purple}`,
                    }}
                    data-testid="readonly-view-memberships">
              View Memberships
            </button>
            <button onClick={() => navigate("/checkin/history")}
                    className="rounded-full px-4 py-1.5 text-[12px] underline"
                    style={{ color: "#6C6478" }}
                    data-testid="readonly-back-history">
              Back to My Check-Ins
            </button>
          </div>
        </div>
      )}

      <div className="mt-4">
        {!readOnlyMode && (
          <ComplimentaryBanner
            state={convState}
            accessType={accessType}
            invitation={invitation}
            balance={balance}
            hasMembership={hasMembership}
            onResumePaid={onResumePaid}
            resuming={resuming}
          />
        )}
      </div>

      <div
        ref={listRef}
        className="mt-3 flex-1 overflow-y-auto pb-4"
        style={{ minHeight: 320 }}
        data-testid="message-list"
      >
        {messages.map((m) => (
          <Bubble
            key={m.id} msg={m}
            onPlayAudio={onPlayAudio}
            playing={audioMsgId === m.id && audioPlaying}
            loadingAudio={loadingAudioFor === m.id}
          />
        ))}
        {sending && (
          <div className="w-full flex justify-start mt-3" data-testid="typing-indicator">
            <div className="mr-2 flex-shrink-0 mt-1"><DrCAvatar small /></div>
            <div className="rounded-2xl px-4 py-3 border"
                 style={{ background: "white", borderColor: CHECKIN_PALETTE.silverSoft }}>
              <span className="inline-flex gap-1 items-center text-[13px]" style={{ color: "#6C6478" }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: CHECKIN_PALETTE.purple }} />
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: CHECKIN_PALETTE.purple, animationDelay: "0.2s" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: CHECKIN_PALETTE.purple, animationDelay: "0.4s" }} />
              </span>
            </div>
          </div>
        )}
      </div>

      {err ? (
        <div className="mb-2 text-center text-[12px]"
             style={{ color: CHECKIN_PALETTE.danger }}
             data-testid="conversation-error">{err}</div>
      ) : null}

      {convState !== "paused_awaiting_paid" && !readOnlyMode && (
        <div className="mt-auto sticky bottom-0 pb-2 pt-2"
             style={{ background: CHECKIN_PALETTE.paper }}>
          <div className="flex items-end gap-2 rounded-full border px-3 py-2"
               style={{ background: "white", borderColor: CHECKIN_PALETTE.silver }}
               data-testid="composer">
            <button
              type="button" onClick={toggleMic}
              data-testid="composer-mic"
              className="flex-shrink-0 rounded-full w-9 h-9 flex items-center justify-center"
              style={{
                background: micActive ? CHECKIN_PALETTE.purple : "transparent",
                color: micActive ? "white" : CHECKIN_PALETTE.purple,
              }}
              aria-label="Voice input"
            >
              {micActive ? <StopCircle className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={1}
              placeholder="Say “Hey Dr. C…”"
              data-testid="composer-input"
              className="flex-1 resize-none border-0 outline-none text-[14.5px] py-1.5 max-h-24"
              style={{ background: "transparent", color: CHECKIN_PALETTE.ink }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
            />
            <button
              type="button" onClick={onSend}
              disabled={sending || !text.trim()}
              data-testid="composer-send"
              className="flex-shrink-0 rounded-full w-10 h-10 flex items-center justify-center text-white disabled:opacity-40"
              style={{ background: CHECKIN_PALETTE.purple }}
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <p className="mt-2 text-center text-[10.5px]" style={{ color: "#8A8299" }}>
            Not an emergency service. Not continuously monitored.
          </p>
        </div>
      )}

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => setAudioPlaying(false)}
          onPause={() => setAudioPlaying(false)}
          onPlay={() => setAudioPlaying(true)}
          data-testid="conversation-audio"
        >
          <track kind="captions" />
        </audio>
      )}
    </CheckInShell>
  );
}
